import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "buttonLabel", "modal", "modalTitle", "costingList"]

  static values = {
    archiveUrl: String,
    frameId: { type: String, default: "product_costings_table" },
  }

  connect() {
    this.updateButton()
    this.element.addEventListener("print-selection:updated", this.boundOnSelectionUpdated)
    document.addEventListener("turbo:frame-render", this.boundOnFrameRender)
    document.addEventListener("turbo:submit-end", this.boundOnSubmitEnd)
  }

  disconnect() {
    this.element.removeEventListener("print-selection:updated", this.boundOnSelectionUpdated)
    document.removeEventListener("turbo:frame-render", this.boundOnFrameRender)
    document.removeEventListener("turbo:submit-end", this.boundOnSubmitEnd)
  }

  get boundOnSelectionUpdated() {
    if (!this._boundOnSelectionUpdated) {
      this._boundOnSelectionUpdated = () => this.updateButton()
    }
    return this._boundOnSelectionUpdated
  }

  get boundOnFrameRender() {
    if (!this._boundOnFrameRender) {
      this._boundOnFrameRender = (event) => {
        if (event.target?.id === this.frameIdValue) {
          setTimeout(() => this.updateButton(), 0)
        }
      }
    }
    return this._boundOnFrameRender
  }

  get boundOnSubmitEnd() {
    if (!this._boundOnSubmitEnd) {
      this._boundOnSubmitEnd = (event) => {
        if (event.detail?.success) {
          setTimeout(() => this.updateButton(), 0)
        }
      }
    }
    return this._boundOnSubmitEnd
  }

  updateButton() {
    if (!this.hasButtonTarget || !this.hasButtonLabelTarget) return

    const { ids, names, count } = this.getSelectedCostings()
    if (count === 0) {
      this.buttonTarget.disabled = true
      this.buttonTarget.classList.add("btn-disabled")
      this.buttonLabelTarget.textContent = "Archive"
    } else {
      this.buttonTarget.disabled = false
      this.buttonTarget.classList.remove("btn-disabled")
      this.buttonLabelTarget.textContent = `Archive (${count})`
    }

    this._pendingIds = ids
    this._pendingNames = names
  }

  getSelectedCostings() {
    const rows = document.querySelectorAll('[data-print-selection-target="row"]')
    const selected = []

    rows.forEach((row) => {
      const checkbox = row.querySelector('[data-print-selection-target="rowCheckbox"]')
      if (!checkbox?.checked) return

      selected.push({
        id: checkbox.value,
        name: row.dataset.productCostingName || checkbox.value,
      })
    })

    return {
      ids: selected.map((item) => item.id),
      names: selected.map((item) => item.name),
      count: selected.length,
    }
  }

  openModal() {
    const { ids, names, count } = this.getSelectedCostings()
    if (count === 0) return

    this._pendingIds = ids
    this._pendingNames = names

    if (this.hasModalTitleTarget) {
      this.modalTitleTarget.textContent = `Archive Product Costings (${count})`
    }
    if (this.hasCostingListTarget) {
      this.costingListTarget.innerHTML = ""
      names.forEach((name) => {
        const badge = document.createElement("span")
        badge.className = "badge badge-outline"
        badge.textContent = name
        this.costingListTarget.appendChild(badge)
      })
    }

    this.modalTarget.showModal()
  }

  closeModal() {
    this.modalTarget.close()
  }

  archive() {
    const ids = this._pendingIds
    if (!ids || ids.length === 0) return

    fetch(this.archiveUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]')?.content,
        Accept: "application/json",
      },
      body: JSON.stringify({ ids }),
    })
      .then((response) => {
        if (response.ok) {
          this.closeModal()
          window.toast?.("Archived successfully.", "success")
          window.Turbo?.visit(window.location.href, { action: "replace" })
        } else {
          window.toast?.("Failed to archive product costings.", "error")
        }
      })
      .catch(() => {
        window.toast?.("Failed to archive product costings.", "error")
      })
  }
}
